import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, TouchableOpacity, Image } from 'react-native-web';
import { useNavigate, useParams } from 'react-router-dom';
import { createDRoll, updateDRoll, getDRollByName, createDRollImage } from '../services/DRollService';
import Header from './Header';
import Button from './Button';
import CustomCheckbox from './CustomCheckbox';
import toast, { Toaster } from 'react-hot-toast';

const DATE_FIELDS = ['startDate', 'endDate', 'invitationAcceptanceEndDate', 'contentDeliveryStartDate', 'contentDeliveryEndDate'];

const parseISOToDateInput = (isoString) => {
    if (!isoString || isoString.startsWith('0001-01-01')) return '';
    return isoString.split('T')[0];
};

const EditDRoll = () => {
    const navigate = useNavigate();
    const { drollSlug } = useParams();
    const isEditing = !!drollSlug;

    const [droll, setDRoll] = useState({
        name: '',
        description: '',
        private: false,
        startDate: '',
        endDate: '',
        invitationAcceptanceEndDate: '',
        contentDeliveryStartDate: '',
        contentDeliveryEndDate: '',
        contentType: 'photos',
        photoCompensation: 0,
        videoCompensation: 0,
        cover_img: null
    });

    const [isLoading, setIsLoading] = useState(false);

    const paymentField = droll.contentType === 'videos' ? 'videoCompensation' : 'photoCompensation';
    const paymentValue = droll[paymentField];

    const coverImageUri = useMemo(() => {
        if (droll.cover_img) {
            return URL.createObjectURL(droll.cover_img);
        }
        return null;
    }, [droll.cover_img]);

    useEffect(() => {
        if (isEditing) {
            getDRollByName(drollSlug)
                .then(resp => {
                    if (resp.status === 200 && resp.data) {
                        const data = { ...resp.data };
                        DATE_FIELDS.forEach(field => {
                            data[field] = parseISOToDateInput(data[field]);
                        });
                        setDRoll(data);
                    }
                })
                .catch(err => {
                    toast.error('Failed to load D-Roll');
                    navigate('/drolls');
                });
        }
    }, [drollSlug, isEditing, navigate]);

    const handleInputChange = (field, value) => {
        setDRoll(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const setCoverImage = (file) => {
        setDRoll(prev => ({
            ...prev,
            cover_img: file
        }));
    };

    const removeCoverImage = () => {
        setDRoll(prev => ({
            ...prev,
            cover_img: null
        }));
    };

    const formatDateForAPI = (dateString) => {
        if (!dateString) return '0001-01-01T00:00:00Z';
        return new Date(dateString).toISOString();
    };

    const handleSubmit = async () => {
        if (!droll.name.trim()) {
            toast.error('D-Roll name is required');
            return;
        }

        setIsLoading(true);

        try {
            const formattedDRoll = {
                ...droll,
                startDate: formatDateForAPI(droll.startDate),
                endDate: formatDateForAPI(droll.endDate),
                invitationAcceptanceEndDate: formatDateForAPI(droll.invitationAcceptanceEndDate),
                contentDeliveryStartDate: formatDateForAPI(droll.contentDeliveryStartDate),
                contentDeliveryEndDate: formatDateForAPI(droll.contentDeliveryEndDate)
            };

            // Remove cover_img from the API request as it's handled separately
            const { cover_img, ...drollData } = formattedDRoll;

            const response = isEditing
                ? await updateDRoll(droll.id, drollData)
                : await createDRoll(drollData);
            if (response.status === 200 || response.status === 201) {
                // Upload cover image if provided
                if (droll.cover_img && response.data && response.data.id) {
                    const formData = new FormData();
                    formData.append("img", droll.cover_img);
                    await createDRollImage(response.data.id, formData);
                }

                toast.success(isEditing ? 'D-Roll updated successfully' : 'D-Roll created successfully');
                navigate('/drolls');
            } else {
                toast.error('Failed to save D-Roll');
            }
        } catch (error) {
            toast.error('Failed to save D-Roll');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <View style={{ height: '100vh' }}>
            <Header title={isEditing ? 'Edit D-Roll' : 'Create D-Roll'} />
            <ScrollView style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Basic Information</Text>
                        
                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>D-Roll Name *</Text>
                            <TextInput
                                style={styles.input}
                                value={droll.name}
                                onChangeText={(value) => handleInputChange('name', value)}
                                placeholder="Enter D-Roll name"
                                placeholderTextColor="#9ca3af"
                            />
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Description</Text>
                            <TextInput
                                style={[styles.input, styles.textArea]}
                                value={droll.description}
                                onChangeText={(value) => handleInputChange('description', value)}
                                placeholder="Describe what kind of content you're looking for"
                                placeholderTextColor="#9ca3af"
                                multiline
                                numberOfLines={4}
                            />
                        </View>

                        <View style={styles.checkboxGroup}>
                            <CustomCheckbox
                                value={!droll.private}
                                onValueChange={(value) => handleInputChange('private', !value)}
                            />
                            <Text style={styles.checkboxLabel}>Public D-Roll (visible to all creators)</Text>
                        </View>
                    </View>

                    <View style={styles.twoColumnContainer}>
                        <View style={[styles.section, styles.columnSection]}>
                            <Text style={styles.sectionTitle}>Cover Image</Text>
                            
                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Upload Cover Image</Text>
                                
                                {!droll.cover_img && !droll.imageExists && (
                                    <>
                                        <View style={styles.fileInputContainer}>
                                            <input 
                                                style={styles.fileInput} 
                                                type="file" 
                                                accept="image/*"
                                                onChange={(event) => {
                                                    setCoverImage(event.target.files[0]);
                                                }} 
                                            />
                                        </View>
                                        <Text style={styles.subtleText}>
                                            For best results, use .jpg, .jpeg, or .png format and a 1:1 aspect ratio.
                                        </Text>
                                    </>
                                )}
                                
                                {droll.cover_img && (
                                    <View style={styles.imageContainer}>
                                        <Image
                                            source={coverImageUri}
                                            style={styles.coverImage}
                                            resizeMode="cover"
                                        />
                                        <TouchableOpacity 
                                            style={styles.removeImageButton}
                                            onPress={removeCoverImage}
                                        >
                                            <Text style={styles.removeImageText}>×</Text>
                                        </TouchableOpacity>
                                    </View>
                                )}
                                
                                {!droll.cover_img && droll.imageExists && (
                                    <View style={styles.imageContainer}>
                                        <Image
                                            source={{uri: `https://d3p3fw3rutb1if.cloudfront.net/business/photos/${droll.id}`}}
                                            style={styles.coverImage}
                                            resizeMode="cover"
                                        />
                                        <TouchableOpacity 
                                            style={styles.removeImageButton}
                                            onPress={removeCoverImage}
                                        >
                                            <Text style={styles.removeImageText}>×</Text>
                                        </TouchableOpacity>
                                    </View>
                                )}
                            </View>
                        </View>

                        <View style={[styles.section, styles.columnSection]}>
                            <Text style={styles.sectionTitle}>Content Type</Text>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Type</Text>
                                <View style={styles.selectContainer}>
                                    <select
                                        style={styles.select}
                                        value={droll.contentType}
                                        onChange={(e) => handleInputChange('contentType', e.target.value)}
                                    >
                                        <option value="photos">Photos</option>
                                        <option value="videos">Videos</option>
                                    </select>
                                </View>
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Payment per Content</Text>
                                <View style={styles.paymentInputWrapper}>
                                    <Text style={styles.dollarSign}>$</Text>
                                    <TextInput
                                        style={styles.paymentInput}
                                        value={(paymentValue ?? '').toString()}
                                        onChangeText={(text) => {
                                            const numericValue = parseFloat(text) || 0;
                                            handleInputChange(paymentField, numericValue);
                                        }}
                                        keyboardType="numeric"
                                        placeholder="0.00"
                                        placeholderTextColor="#9ca3af"
                                    />
                                </View>
                            </View>
                        </View>
                    </View>

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Campaign Dates</Text>

                        <View style={styles.dateContainer}>
                            <View style={styles.dateInputGroup}>
                                <Text style={styles.label}>Start Date</Text>
                                <input
                                    type="date"
                                    style={styles.dateInput}
                                    value={droll.startDate}
                                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                                />
                            </View>

                            <View style={styles.dateInputGroup}>
                                <Text style={styles.label}>End Date</Text>
                                <input
                                    type="date"
                                    style={styles.dateInput}
                                    value={droll.endDate}
                                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                                />
                            </View>
                        </View>

                        <View style={styles.dateContainer}>
                            <View style={styles.dateInputGroup}>
                                <Text style={styles.label}>Invitation Acceptance End Date</Text>
                                <input
                                    type="date"
                                    style={styles.dateInput}
                                    value={droll.invitationAcceptanceEndDate}
                                    onChange={(e) => handleInputChange('invitationAcceptanceEndDate', e.target.value)}
                                />
                            </View>
                        </View>

                        <View style={styles.dateContainer}>
                            <View style={styles.dateInputGroup}>
                                <Text style={styles.label}>Content Delivery Start Date</Text>
                                <input
                                    type="date"
                                    style={styles.dateInput}
                                    value={droll.contentDeliveryStartDate}
                                    onChange={(e) => handleInputChange('contentDeliveryStartDate', e.target.value)}
                                />
                            </View>

                            <View style={styles.dateInputGroup}>
                                <Text style={styles.label}>Content Delivery End Date</Text>
                                <input
                                    type="date"
                                    style={styles.dateInput}
                                    value={droll.contentDeliveryEndDate}
                                    onChange={(e) => handleInputChange('contentDeliveryEndDate', e.target.value)}
                                />
                            </View>
                        </View>
                    </View>

                    <View style={styles.buttonContainer}>
                        <Button
                            variant="secondary"
                            text="Cancel"
                            onPress={() => navigate('/drolls')}
                            style={styles.cancelButton}
                        />
                        <Button
                            text={isEditing ? 'Update D-Roll' : 'Create D-Roll'}
                            onPress={handleSubmit}
                            disabled={isLoading}
                            style={styles.submitButton}
                        />
                    </View>
                </View>
            </ScrollView>

            <Toaster
                position="top-center"
                reverseOrder={false}
                gutter={8}
                toastOptions={{
                    duration: 3000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 2000,
                        theme: {
                            primary: 'green',
                            secondary: 'black',
                        },
                    },
                }}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f1f1f1',
    },
    formContainer: {
        maxWidth: 800,
        margin: '0 auto',
        padding: 20,
    },
    section: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
        marginBottom: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    sectionTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 20,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 20,
    },
    inputGroup: {
        marginBottom: 20,
    },
    label: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        fontWeight: '600',
        color: '#374151',
        marginBottom: 8,
    },
    input: {
        borderWidth: 2,
        borderColor: '#e5e7eb',
        borderRadius: 8,
        padding: 12,
        fontSize: 16,
        fontFamily: "'lato', sans-serif",
        backgroundColor: 'white',
        outline: 'none',
    },
    textArea: {
        minHeight: 100,
        textAlignVertical: 'top',
    },
    fileInput: {
        fontFamily: "'lato', sans-serif",
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        cursor: 'pointer',
        fontSize: 16,
        color: '#374151',
    },
    subtleText: {
        fontSize: 14,
        color: '#6b7280',
        fontFamily: "'lato', sans-serif",
        marginTop: 8,
    },
    coverImage: {
        width: 150,
        height: 150,
        borderRadius: 8,
        alignSelf: 'center',
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    checkboxGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    checkboxLabel: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#374151',
        marginLeft: 12,
    },
    paymentInputWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    dollarSign: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        fontWeight: '600',
        color: '#396de0',
        marginRight: 8,
    },
    paymentInput: {
        borderWidth: 2,
        borderColor: '#b8cbf5',
        borderRadius: 6,
        padding: 8,
        fontSize: 16,
        fontFamily: "'lato', sans-serif",
        backgroundColor: 'white',
        minWidth: 100,
        textAlign: 'center',
        outline: 'none',
    },
    selectContainer: {
        borderWidth: 2,
        borderColor: '#e5e7eb',
        borderRadius: 8,
        backgroundColor: 'white',
    },
    select: {
        width: '100%',
        padding: 12,
        fontSize: 16,
        fontFamily: "'lato', sans-serif",
        border: 'none',
        outline: 'none',
        backgroundColor: 'transparent',
    },
    dateInput: {
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: '#e5e7eb',
        borderRadius: 8,
        padding: 12,
        fontSize: 16,
        fontFamily: "'lato', sans-serif",
        backgroundColor: 'white',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        color: '#374151',
    },
    dateContainer: {
        flexDirection: 'row',
        gap: 20,
        marginBottom: 16,
    },
    dateInputGroup: {
        flex: 1,
    },
    buttonContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 16,
        marginTop: 20,
    },
    cancelButton: {
        minWidth: 120,
    },
    submitButton: {
        minWidth: 120,
    },
    imageContainer: {
        position: 'relative',
        alignSelf: 'center',
        marginTop: 16,
    },
    removeImageButton: {
        position: 'absolute',
        top: -8,
        right: -8,
        backgroundColor: '#ef4444',
        width: 24,
        height: 24,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
        borderWidth: 2,
        borderColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ':hover': {
            backgroundColor: '#dc2626',
            transform: 'scale(1.1)',
            shadowOpacity: 0.35,
        },
    },
    removeImageText: {
        color: 'white',
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: 16,
    },
    twoColumnContainer: {
        flexDirection: 'row',
        gap: 20,
        marginBottom: 20,
        '@media (max-width: 768px)': {
            flexDirection: 'column',
        },
    },
    columnSection: {
        flex: 1,
        marginBottom: 0,
    },
    fileInputContainer: {
        borderWidth: 0,
        borderRadius: 8,
        padding: 16,
        backgroundColor: 'transparent',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 80,
    },
});

export default EditDRoll; 